import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Briefcase, FileText, Car, Clock, CheckCircle, AlertCircle,
  DollarSign, Calendar, ArrowRight, ArrowUpRight
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { usePortalAuth } from "./PortalLayout";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PortalDashboard = () => {
  const { customer, business } = usePortalAuth();
  const [stats, setStats] = useState({ totalJobs: 0, activeJobs: 0, totalInvoices: 0, unpaidInvoices: 0, unpaidAmount: 0, vehicles: 0 });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem("portal_token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const [jobsRes, invoicesRes, vehiclesRes] = await Promise.all([
          axios.get(`${API}/portal/jobs?limit=5`, config),
          axios.get(`${API}/portal/invoices?limit=5`, config),
          axios.get(`${API}/portal/vehicles`, config),
        ]);
        setRecentJobs(jobsRes.data.jobs);
        setRecentInvoices(invoicesRes.data.invoices);

        const activeJobs = jobsRes.data.jobs.filter(j => ["scheduled", "in_progress", "pending"].includes(j.status)).length;
        const unpaidInvoices = invoicesRes.data.invoices.filter(i => ["pending", "sent", "overdue"].includes(i.status));
        const unpaidAmount = unpaidInvoices.reduce((sum, i) => sum + (i.total || 0), 0);

        setStats({
          totalJobs: jobsRes.data.total, activeJobs,
          totalInvoices: invoicesRes.data.total,
          unpaidInvoices: unpaidInvoices.length, unpaidAmount,
          vehicles: vehiclesRes.data.vehicles.length,
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const getStatusColor = (status) => {
    const map = { completed: "#22C55E", in_progress: "#3B82F6", scheduled: "#8B5CF6", pending: "#F59E0B", cancelled: "#EF4444", paid: "#22C55E", sent: "#3B82F6", overdue: "#EF4444", draft: "#94A3B8" };
    return map[status] || "#94A3B8";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-7 h-7 border-2 border-[#0046BE]/20 border-t-[#0046BE] rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="portal-dashboard">
      {/* Welcome */}
      <div className="relative overflow-hidden rounded-2xl p-6 sm:p-8" style={{background: 'linear-gradient(135deg, #0046BE 0%, #0033A0 100%)'}}>
        <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: 'radial-gradient(circle at 30% 40%, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        <div className="relative">
          <h1 className="text-xl sm:text-2xl font-bold text-white font-['Outfit']" data-testid="portal-welcome">
            Welcome back, {customer?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="text-blue-100/70 text-sm mt-1">
            Your account overview with {business?.business_name || "us"}
          </p>
        </div>
      </div>

      {/* Amount Due CTA — primary action for unpaid customers */}
      {stats.unpaidInvoices > 0 && (
        <Link
          to="/portal/invoices?status=unpaid"
          className="group block rounded-2xl p-5 sm:p-6 border-2 transition-all hover:translate-y-[-2px]"
          style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderColor: '#f59e0b',
            boxShadow: '0 4px 14px rgba(245, 158, 11, 0.15)'
          }}
          data-testid="portal-amount-due-cta"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#f59e0b] flex items-center justify-center shadow-sm">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-xs font-semibold text-[#92400e] uppercase tracking-wider">Amount Due</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#78350f] font-['Outfit']">
                  ${stats.unpaidAmount.toFixed(2)}
                </p>
                <p className="text-xs text-[#92400e]/80 mt-0.5">
                  {stats.unpaidInvoices} {stats.unpaidInvoices === 1 ? "invoice" : "invoices"} pending payment
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl shadow-sm text-[#92400e] font-semibold text-sm group-hover:bg-[#78350f] group-hover:text-white transition-colors">
              Pay Now <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { icon: Briefcase, label: "Total Jobs", value: stats.totalJobs, color: "#0046BE" },
          { icon: Clock, label: "Active", value: stats.activeJobs, color: "#8B5CF6" },
          { icon: FileText, label: "Unpaid", value: stats.unpaidInvoices, color: "#F59E0B" },
          { icon: DollarSign, label: "Amount Due", value: `$${stats.unpaidAmount.toFixed(2)}`, color: "#EF4444" },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl p-4 sm:p-5 transition-all duration-200 hover:translate-y-[-2px]" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}} data-testid={`portal-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background: `${stat.color}08`}}>
                <stat.icon className="w-[18px] h-[18px]" style={{color: stat.color}} />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-medium font-['Outfit']">{stat.label}</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-800 mt-0.5 font-['Outfit']">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Jobs */}
        <div className="bg-white rounded-2xl overflow-hidden" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
          <div className="flex items-center justify-between p-4 sm:p-5 pb-3">
            <h2 className="text-sm font-semibold text-slate-700 font-['Outfit']">Recent Jobs</h2>
            <Link to="/portal/jobs" className="text-xs text-[#0046BE] hover:text-[#0033A0] font-medium flex items-center gap-1 transition-colors" data-testid="portal-view-all-jobs">
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            {recentJobs.length === 0 ? (
              <div className="text-center py-8"><Briefcase className="w-8 h-8 mx-auto text-slate-200 mb-2" /><p className="text-slate-300 text-sm">No jobs yet</p></div>
            ) : (
              <div className="space-y-2">
                {recentJobs.slice(0, 4).map((job) => (
                  <Link key={job.job_id} to={`/portal/jobs/${job.job_id}`} className="group flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`portal-recent-job-${job.job_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-1 h-8 rounded-full flex-shrink-0" style={{background: getStatusColor(job.status)}} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{job.title || job.job_type_name || "Service"}</p>
                        <p className="text-xs text-slate-300 mt-0.5">{formatDate(job.scheduled_date || job.created_at)}</p>
                      </div>
                    </div>
                    <Badge className="text-[10px] px-2 py-0.5 flex-shrink-0 ml-2 border" style={{background: `${getStatusColor(job.status)}08`, color: getStatusColor(job.status), borderColor: `${getStatusColor(job.status)}20`}}>
                      {job.status?.replace("_", " ")}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Invoices */}
        <div className="bg-white rounded-2xl overflow-hidden" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
          <div className="flex items-center justify-between p-4 sm:p-5 pb-3">
            <h2 className="text-sm font-semibold text-slate-700 font-['Outfit']">Recent Invoices</h2>
            <Link to="/portal/invoices" className="text-xs text-[#0046BE] hover:text-[#0033A0] font-medium flex items-center gap-1 transition-colors" data-testid="portal-view-all-invoices">
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            {recentInvoices.length === 0 ? (
              <div className="text-center py-8"><FileText className="w-8 h-8 mx-auto text-slate-200 mb-2" /><p className="text-slate-300 text-sm">No invoices yet</p></div>
            ) : (
              <div className="space-y-2">
                {recentInvoices.slice(0, 4).map((invoice) => (
                  <Link key={invoice.invoice_id} to={`/portal/invoices/${invoice.invoice_id}`} className="group flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`portal-recent-invoice-${invoice.invoice_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-1 h-8 rounded-full flex-shrink-0" style={{background: getStatusColor(invoice.status)}} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">#{invoice.invoice_number || invoice.invoice_id?.slice(0, 8)}</p>
                        <p className="text-xs text-slate-300 mt-0.5">{formatDate(invoice.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                      <span className="text-sm font-semibold text-slate-700">${(invoice.total || 0).toFixed(2)}</span>
                      <Badge className="text-[10px] px-2 py-0.5 border" style={{background: `${getStatusColor(invoice.status)}08`, color: getStatusColor(invoice.status), borderColor: `${getStatusColor(invoice.status)}20`}}>
                        {invoice.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-4 sm:p-5" style={{boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)'}}>
        <h2 className="text-sm font-semibold text-slate-700 font-['Outfit'] mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { to: "/portal/jobs", icon: Briefcase, label: "View Jobs", desc: "See service history", color: "#0046BE" },
            { to: "/portal/invoices", icon: FileText, label: "View Invoices", desc: "Check payment status", color: "#22C55E" },
            { to: "/portal/vehicles", icon: Car, label: "My Vehicles", desc: "View registered vehicles", color: "#8B5CF6" },
          ].map((action) => (
            <Link key={action.to} to={action.to} className="group flex items-center gap-3 p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 transition-all" data-testid={`portal-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-200 group-hover:scale-105" style={{background: `${action.color}08`}}>
                <action.icon className="w-5 h-5" style={{color: action.color}} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 font-['Outfit']">{action.label}</p>
                <p className="text-xs text-slate-300">{action.desc}</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-slate-200 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortalDashboard;
